import React, { useState, useEffect, useContext } from 'react';
import { PDFDownloadLink, PDFViewer, pdf } from '@react-pdf/renderer';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, IconButton, Tooltip, CircularProgress,
  useMediaQuery, useTheme, Typography, Alert
} from '@mui/material';
import {
  Close, Download, Print, Chat, Visibility
} from '@mui/icons-material';
import InvoicePDF from './InvoicePDF';
import { getCompanyConfig, getCompanyConfigFromAPI } from './CompanyConfig';
import { useAuth } from '../../../AuthContext';

// Error Boundary Component for PDF rendering
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('PDF Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          <Typography variant="h6">PDF Generation Error</Typography>
          <Typography variant="body2">
            Unable to generate PDF preview. This might be due to:
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Network connectivity issues</li>
            <li>Font loading problems</li>
            <li>Invalid data format</li>
          </ul>
          <Typography variant="body2">
            Please try refreshing the page or contact support if the issue persists.
          </Typography>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={() => this.setState({ hasError: false, error: null })}
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        </Alert>
      );
    }

    return this.props.children;
  }
}

const InvoicePDFViewer = ({
  open,
  onClose,
  saleData,
  saleDetails,
  customerData,
  companyData,
  items = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { token, companyId: authCompanyId, branchId: authBranchId } = useAuth();

  // Use company data from props or fetch if not provided
  const [fetchedCompanyData, setFetchedCompanyData] = useState(null);
  const [companyDataLoading, setCompanyDataLoading] = useState(false);

  // Fetch company data from API only if not provided as prop
  useEffect(() => {
    const fetchCompanyDataIfNeeded = async () => {
      // If companyData is already provided as prop, use it
      if (companyData) {
        setFetchedCompanyData(companyData);
        return;
      }

      // Try to get company and branch IDs from saleData or auth context
      const companyId = saleData?.CompanyId || 
                       saleData?.companyId || 
                       authCompanyId;

      const branchId = saleData?.BranchId || 
                      saleData?.branchId || 
                      authBranchId;

      // If we have the required IDs and token, fetch from API
      if (companyId && branchId && token) {
        setCompanyDataLoading(true);
        try {
          console.log('Fetching company data from API...', { companyId, branchId });
          const apiCompanyData = await getCompanyConfigFromAPI(companyId, branchId, token);
          setFetchedCompanyData(apiCompanyData);
          console.log('Company data fetched successfully:', apiCompanyData);
        } catch (error) {
          console.error('Failed to fetch company data from API:', error);
          // Fallback to default config
          setFetchedCompanyData(getCompanyConfig());
        } finally {
          setCompanyDataLoading(false);
        }
      } else {
        console.log('Using default company config - missing:', {
          companyId: !companyId,
          branchId: !branchId,
          token: !token,
          authCompanyId,
          authBranchId
        });
        // Use default config if no IDs or token available
        setFetchedCompanyData(getCompanyConfig());
      }
    };

    if (open && saleData) {
      fetchCompanyDataIfNeeded();
    }
  }, [open, saleData, companyData, token, authCompanyId, authBranchId]);

  // Enhance sale details with item names
  const enhancedSaleDetails = saleDetails?.map(detail => ({
    ...detail,
    ItemName: items.find(item => item.Id === parseInt(detail.ItemId))?.Name || `Item ${detail.ItemId}`
  })) || [];

  // Use fetched company data or fallback to default
  const finalCompanyData = fetchedCompanyData || getCompanyConfig();

  // Generate PDF blob for sharing/emailing
  const generatePDFBlob = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      
      const blob = await pdf(
        <InvoicePDF
          saleData={saleData}
          saleDetails={enhancedSaleDetails}
          customerData={customerData}
          companyData={finalCompanyData}
        />
      ).toBlob();
      
      setLoading(false);
      return blob;
    } catch (err) {
      console.error('PDF generation error:', err);
      setError(`Failed to generate PDF: ${err.message || 'Unknown error'}`);
      setLoading(false);
      return null;
    }
  };

  // Handle print
  const handlePrint = async () => {
    try {
      const blob = await generatePDFBlob();
      if (blob) {
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
      }
    } catch (err) {
      setError('Failed to print PDF');
    }
  };

  // Handle WhatsApp sharing
  const handleWhatsApp = async () => {
    try {
      const blob = await generatePDFBlob();
      if (blob) {
        // Create a formatted message for WhatsApp
        const message = `🧾 *Invoice #${saleData.BillNumber}*\n` +
                       `From: ${finalCompanyData.name}\n\n` +
                       `📋 *Invoice Details:*\n` +
                       `• Invoice Number: ${saleData.BillNumber}\n` +
                       `• Date: ${new Date(saleData.BillDate).toLocaleDateString('en-IN')}\n` +
                       `• Customer: ${saleData.CustomerName}\n` +
                       `• Amount: ₹${saleData.TotalAmount?.toLocaleString('en-IN')}\n\n` +
                       `💰 *Payment Status:*\n` +
                       `• Paid: ₹${saleData.PaidAmount?.toLocaleString('en-IN')}\n` +
                       `• Balance: ₹${saleData.BalanceAmount?.toLocaleString('en-IN')}\n\n` +
                       `Thank you for your business! 🙏\n\n` +
                       `📎 *Invoice PDF will be downloaded to your device. Please attach it manually to this WhatsApp chat.*`;

        // For mobile devices with WhatsApp app, try Web Share API first
        if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          try {
            const file = new File([blob], `Invoice-${saleData.BillNumber}.pdf`, { type: 'application/pdf' });
            
            // Check if we can share files
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
              await navigator.share({
                title: `Invoice #${saleData.BillNumber}`,
                text: message.replace('📎 *Invoice PDF will be downloaded to your device. Please attach it manually to this WhatsApp chat.*', '📎 *Invoice PDF attached*'),
                files: [file]
              });
              return; // Success - file was shared with attachment
            }
          } catch (shareError) {
            console.log('Web Share with file failed, falling back to WhatsApp Web');
          }
        }

        // Download PDF first
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Invoice-${saleData.BillNumber}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Small delay to ensure download starts
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);

        // Then open WhatsApp with message
        const encodedMessage = encodeURIComponent(message);
        
        // Check if customer has a phone number to send directly
        let whatsappUrl;
        if (saleData.ContactNumber1) {
          // Clean phone number (remove spaces, dashes, etc.)
          const cleanPhone = saleData.ContactNumber1.replace(/\D/g, '');
          // Add country code if not present (assuming India +91)
          const phoneWithCountry = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`;
          whatsappUrl = `https://wa.me/${phoneWithCountry}?text=${encodedMessage}`;
        } else {
          // General WhatsApp Web link
          whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
        }
        
        // Small delay before opening WhatsApp to ensure download completes
        setTimeout(() => {
          window.open(whatsappUrl, '_blank');
        }, 500);

        // Show user instruction
        setError(''); // Clear any errors
        setSuccess('PDF downloaded! WhatsApp opened. Please attach the downloaded PDF file manually in the WhatsApp chat.');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess('');
        }, 5000);
      }
    } catch (err) {
      console.error('WhatsApp sharing error:', err);
      setError('Failed to share via WhatsApp. Please try again.');
    }
  };

  // Handle share (Web Share API) - This will show Email, Telegram, etc.
  const handleShare = async () => {
    try {
      const blob = await generatePDFBlob();
      if (blob && navigator.share) {
        // Create a formatted message for sharing
        const message = `🧾 Invoice #${saleData.BillNumber}\n` +
                       `From: ${finalCompanyData.name}\n\n` +
                       `📋 Invoice Details:\n` +
                       `• Invoice Number: ${saleData.BillNumber}\n` +
                       `• Date: ${new Date(saleData.BillDate).toLocaleDateString('en-IN')}\n` +
                       `• Customer: ${saleData.CustomerName}\n` +
                       `• Amount: ₹${saleData.TotalAmount?.toLocaleString('en-IN')}\n\n` +
                       `💰 Payment Status:\n` +
                       `• Paid: ₹${saleData.PaidAmount?.toLocaleString('en-IN')}\n` +
                       `• Balance: ₹${saleData.BalanceAmount?.toLocaleString('en-IN')}\n\n` +
                       `Thank you for your business! 🙏`;

        const file = new File([blob], `Invoice-${saleData.BillNumber}.pdf`, { type: 'application/pdf' });
        
        // Try sharing text first (this will show more messaging apps)
        try {
          await navigator.share({
            title: `Invoice #${saleData.BillNumber} - ${finalCompanyData.name}`,
            text: message
          });
          
          // If text sharing succeeds, also download the PDF
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `Invoice-${saleData.BillNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
        } catch (textShareError) {
          // If text sharing fails, try with file
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Invoice #${saleData.BillNumber} - ${finalCompanyData.name}`,
              files: [file]
            });
          } else {
            throw new Error('File sharing not supported');
          }
        }
      } else {
        // Fallback for browsers that don't support Web Share API
        setError('Sharing not supported on this browser. Please use the download button instead.');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // User cancelled the share - this is normal, don't show error
        return;
      }
      console.error('Share error:', err);
      setError('Failed to share. Please try again or use the download button.');
    }
  };

  // Helper function to convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const fileName = `Invoice-${saleData?.BillNumber || 'Draft'}-${new Date().toISOString().split('T')[0]}.pdf`;

  if (!saleData) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Alert severity="error">No sale data provided for PDF generation.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Show loading while fetching company data
  if (companyDataLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading company information...
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          m: isMobile ? 0 : 2,
          maxHeight: '95vh',
          width: isMobile ? '100%' : '90%',
          maxWidth: '1200px'
        }
      }}
    >
      <DialogTitle sx={{
        py: 1.5,
        px: 2,
        borderBottom: '1px solid #e5e7eb',
        bgcolor: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Visibility sx={{ color: '#1e40af', fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e40af' }}>
            Invoice Preview - #{saleData.BillNumber}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Action Buttons - Only Download, Print, and WhatsApp */}
          <Tooltip title="Download PDF">
            <span>
              <PDFDownloadLink
                document={
                  <InvoicePDF
                    saleData={saleData}
                    saleDetails={enhancedSaleDetails}
                    customerData={customerData}
                    companyData={finalCompanyData}
                  />
                }
                fileName={fileName}
                style={{ textDecoration: 'none' }}
              >
                {({ loading: downloadLoading }) => (
                  <IconButton
                    size="small"
                    disabled={downloadLoading}
                    sx={{ color: '#059669' }}
                  >
                    {downloadLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Download fontSize="small" />
                    )}
                  </IconButton>
                )}
              </PDFDownloadLink>
            </span>
          </Tooltip>

          <Tooltip title="Print">
            <IconButton
              size="small"
              onClick={handlePrint}
              disabled={loading}
              sx={{ color: '#7c3aed' }}
            >
              {loading ? <CircularProgress size={16} /> : <Print fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Tooltip title="WhatsApp">
            <IconButton
              size="small"
              onClick={handleWhatsApp}
              disabled={loading}
              sx={{ color: '#25d366' }}
            >
              <Chat fontSize="small" />
            </IconButton>
          </Tooltip>

          <IconButton onClick={onClose} size="small" sx={{ color: '#6b7280' }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#f3f4f6' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ m: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ height: isMobile ? 'calc(100vh - 120px)' : '70vh' }}>
          {/* Wrap PDFViewer in error boundary */}
          <ErrorBoundary>
            <PDFViewer
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              showToolbar={!isMobile}
            >
              <InvoicePDF
                saleData={saleData}
                saleDetails={enhancedSaleDetails}
                customerData={customerData}
                companyData={finalCompanyData}
              />
            </PDFViewer>
          </ErrorBoundary>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 2,
        py: 1.5,
        borderTop: '1px solid #e5e7eb',
        bgcolor: '#f8fafc',
        justifyContent: 'space-between'
      }}>
        <Typography variant="caption" sx={{ color: '#6b7280' }}>
          Invoice generated on {new Date().toLocaleString()}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} variant="outlined" size="small">
            Close
          </Button>
          
          <PDFDownloadLink
            document={
              <InvoicePDF
                saleData={saleData}
                saleDetails={enhancedSaleDetails}
                customerData={customerData}
                companyData={finalCompanyData}
              />
            }
            fileName={fileName}
            style={{ textDecoration: 'none' }}
          >
            {({ loading: downloadLoading }) => (
              <Button
                variant="contained"
                size="small"
                disabled={downloadLoading}
                startIcon={downloadLoading ? <CircularProgress size={16} /> : <Download />}
                sx={{
                  bgcolor: '#1e40af',
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                {downloadLoading ? 'Generating...' : 'Download'}
              </Button>
            )}
          </PDFDownloadLink>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePDFViewer;